import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getStockLedger } from "../../services/api/stocks/stockService";

const StockLedger = () => {
  const { id } = useParams();

  const [ledger, setLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchLedger();
  }, []);

  const fetchLedger = async () => {
    try {
      const res = await getStockLedger(id);
      setLedger(res.data.data);
      setFilteredLedger(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let filtered = ledger;

    if (search) {
      filtered = filtered.filter(
        (item) =>
          item.reference_type
            ?.toLowerCase()
            .includes(search.toLowerCase()) ||
          item.reference_id?.toString().includes(search)
      );
    }

    if (filterType !== "all") {
      filtered = filtered.filter(
        (item) => item.type === filterType
      );
    }

    setFilteredLedger(filtered);
    setCurrentPage(1);
  }, [search, filterType, ledger]);

  const totalPages = Math.ceil(
    filteredLedger.length / itemsPerPage
  );

  const paginatedData = filteredLedger.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const openLedgerModal = (item) => {
    setSelectedLedger(item);
    setShowModal(true);
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-5 flex flex-col md:flex-row justify-between gap-4">

        <h2 className="text-2xl font-bold text-gray-800">
          Stock Ledger
        </h2>

        <div className="flex flex-col md:flex-row gap-3">

          <input
            type="text"
            placeholder="Search reference..."
            className="border px-4 py-2 rounded-lg w-full md:w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="border px-4 py-2 rounded-lg"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All</option>
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
          </select>

        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">

        <div className="overflow-x-auto">
          <table className="min-w-full">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Type</th>
                <th className="p-4 text-left">Qty</th>
                <th className="p-4 text-left">Reference</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="p-4">
                      {formatDate(item.created_at)}
                    </td>

                    <td className="p-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.type === "IN"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {item.type}
                      </span>
                    </td>

                    <td className="p-4 font-medium">
                      {item.quantity}
                    </td>

                    <td className="p-4">
                      <button
                        onClick={() => openLedgerModal(item)}
                        className="text-blue-600 hover:underline"
                      >
                        {item.reference_type} #{item.reference_id}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="p-5 text-center text-gray-500"
                  >
                    No ledger found
                  </td>
                </tr>
              )}
            </tbody>

          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center gap-2 p-4 flex-wrap">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={`px-3 py-1 rounded-lg ${
                currentPage === i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {paginatedData.length > 0 ? (
          paginatedData.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow-sm border p-4"
            >
              <div className="flex justify-between items-center">

                <span className="text-sm text-gray-500">
                  {formatDate(item.created_at)}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-xs ${
                    item.type === "IN"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {item.type}
                </span>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <p>
                  Qty:{" "}
                  <span className="font-medium">
                    {item.quantity}
                  </span>
                </p>

                <p>
                  Ref:
                  <button
                    onClick={() => openLedgerModal(item)}
                    className="text-blue-600 ml-1"
                  >
                    {item.reference_type} #{item.reference_id}
                  </button>
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl p-4 text-center text-gray-500">
            No ledger found
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedLedger && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">

          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">

            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold">
                Ledger Transaction Details
              </h3>

              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 text-xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 text-sm">

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-500">Type</p>
                <p className="font-medium">
                  {selectedLedger.type}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-500">Quantity</p>
                <p className="font-medium">
                  {selectedLedger.quantity}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-500">Reference</p>
                <p className="font-medium">
                  {selectedLedger.reference_type} #
                  {selectedLedger.reference_id}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-500">Created At</p>
                <p className="font-medium">
                  {formatDate(selectedLedger.created_at)}
                </p>
              </div>

            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default StockLedger;